/**
 * NobleInput - Inset Style Input Component
 * 
 * Industrial-grade input field with carved metal effect
 * Features:
 * - Inset style (looks carved into metal)
 * - Floating labels on focus
 * - Enhanced focus states with 4px blue glow
 * - Error shake animation
 * - 48px height for glove-friendly operation
 * 
 * @version 1.0.0
 * @date December 1, 2025
 */

import React, { useState } from 'react';
import { Input, InputNumber, InputNumberProps } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import styled from '@emotion/styled';
import { NobleTokens } from '../../../theme/noble-tokens';

const { TextArea } = Input;

export interface NobleInputProps {
  label?: string;
  error?: string;
  size?: 'small' | 'medium' | 'large';
  helperText?: string;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface NobleTextAreaProps {
  label?: string;
  error?: string;
  helperText?: string;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  rows?: number;
  required?: boolean;
  disabled?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

export interface NobleInputNumberProps {
  label?: string;
  error?: string;
  size?: 'small' | 'medium' | 'large';
  helperText?: string;
  required?: boolean;
  value?: number;
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  disabled?: boolean;
  onChange?: (value: number | null) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${NobleTokens.spacing.xs};
  width: 100%;
`;

const InputContainer = styled(motion.div)<{ $hasError: boolean }>`
  position: relative;
  width: 100%;
`;

const StyledInput = styled(Input)<{
  $size: 'small' | 'medium' | 'large';
  $hasError: boolean;
  $hasValue: boolean;
  $isFocused: boolean;
}>`
  /* Base Styles */
  background: ${NobleTokens.colors.background.level0Light};
  border: 2px solid ${({ $hasError, $isFocused }) =>
    $hasError
      ? NobleTokens.colors.semantic.criticalRed
      : $isFocused
      ? NobleTokens.colors.primary.commandBlue
      : 'transparent'};
  border-radius: ${NobleTokens.borderRadius.md};
  box-shadow: ${({ $hasError, $isFocused }) =>
    $hasError
      ? NobleTokens.shadows.focusRed
      : $isFocused
      ? NobleTokens.shadows.focusBlue
      : NobleTokens.shadows.neumorphicInset};
  transition: all ${NobleTokens.animation.duration.fast} ${NobleTokens.animation.easing.easeOut};
  
  font-family: ${NobleTokens.typography.fontFamily.primary};
  font-size: ${NobleTokens.typography.fontSize.body};
  color: ${NobleTokens.colors.text.primary};
  
  /* Size Variants */
  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return `
          height: ${NobleTokens.components.input.height.small};
          padding: ${NobleTokens.components.input.padding};
        `;
      case 'large':
        return `
          height: ${NobleTokens.components.input.height.large};
          padding: ${NobleTokens.components.input.padding};
        `;
      default:
        return `
          height: ${NobleTokens.components.input.height.medium};
          padding: ${NobleTokens.components.input.padding};
        `;
    }
  }}
  
  /* Focus State */
  &:focus,
  &:focus-within {
    background: ${NobleTokens.colors.background.level1Light};
    outline: none;
  }
  
  /* Placeholder */
  &::placeholder {
    color: ${NobleTokens.colors.text.disabled};
    opacity: 0.6;
  }
  
  /* Disabled State */
  &:disabled {
    background: ${NobleTokens.colors.background.level0Light};
    color: ${NobleTokens.colors.text.disabled};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const StyledTextArea = styled(TextArea)<{
  $hasError: boolean;
  $isFocused: boolean;
}>`
  background: ${NobleTokens.colors.background.level0Light};
  border: 2px solid ${({ $hasError, $isFocused }) =>
    $hasError
      ? NobleTokens.colors.semantic.criticalRed
      : $isFocused
      ? NobleTokens.colors.primary.commandBlue
      : 'transparent'};
  border-radius: ${NobleTokens.borderRadius.md};
  box-shadow: ${({ $hasError, $isFocused }) =>
    $hasError
      ? NobleTokens.shadows.focusRed
      : $isFocused
      ? NobleTokens.shadows.focusBlue
      : NobleTokens.shadows.neumorphicInset};
  transition: all ${NobleTokens.animation.duration.fast} ${NobleTokens.animation.easing.easeOut};
  
  font-family: ${NobleTokens.typography.fontFamily.primary};
  font-size: ${NobleTokens.typography.fontSize.body};
  color: ${NobleTokens.colors.text.primary};
  padding: ${NobleTokens.components.input.padding};
  min-height: 120px;
  
  &:focus,
  &:focus-within {
    background: ${NobleTokens.colors.background.level1Light};
    outline: none;
  }
  
  &::placeholder {
    color: ${NobleTokens.colors.text.disabled};
    opacity: 0.6;
  }
  
  &:disabled {
    background: ${NobleTokens.colors.background.level0Light};
    color: ${NobleTokens.colors.text.disabled};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const FloatingLabel = styled(motion.label)<{
  $isFocused: boolean;
  $hasValue: boolean;
  $hasError: boolean;
}>`
  position: absolute;
  left: 16px;
  top: ${({ $isFocused, $hasValue }) => ($isFocused || $hasValue ? '8px' : '50%')};
  transform: translateY(${({ $isFocused, $hasValue }) => ($isFocused || $hasValue ? '0' : '-50%')});
  font-size: ${({ $isFocused, $hasValue }) =>
    $isFocused || $hasValue ? NobleTokens.typography.fontSize.caption : NobleTokens.typography.fontSize.body};
  font-weight: ${NobleTokens.typography.fontWeight.medium};
  color: ${({ $hasError, $isFocused }) =>
    $hasError
      ? NobleTokens.colors.semantic.criticalRed
      : $isFocused
      ? NobleTokens.colors.primary.commandBlue
      : NobleTokens.colors.text.secondary};
  pointer-events: none;
  transition: all ${NobleTokens.animation.duration.fast} ${NobleTokens.animation.easing.easeOut};
  background: ${({ $isFocused, $hasValue }) =>
    $isFocused || $hasValue ? NobleTokens.colors.background.level1Light : 'transparent'};
  padding: ${({ $isFocused, $hasValue }) => ($isFocused || $hasValue ? '0 4px' : '0')};
  z-index: 1;
`;

const HelperText = styled.div<{ $isError: boolean }>`
  font-size: ${NobleTokens.typography.fontSize.caption};
  color: ${({ $isError }) =>
    $isError ? NobleTokens.colors.semantic.criticalRed : NobleTokens.colors.text.secondary};
  margin-top: ${NobleTokens.spacing.micro};
  min-height: 16px;
`;

const RequiredIndicator = styled.span`
  color: ${NobleTokens.colors.semantic.criticalRed};
  margin-left: 4px;
`;

/**
 * NobleInput Component
 */
export const NobleInput: React.FC<NobleInputProps> = ({
  label,
  error,
  helperText,
  size = 'medium',
  value,
  defaultValue,
  required,
  disabled,
  placeholder,
  type,
  onFocus,
  onBlur,
  onChange,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(value || defaultValue || '');
  const hasValue = !!inputValue || value !== undefined;
  const hasError = !!error;

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onChange?.(e);
  };

  return (
    <InputWrapper>
      <InputContainer
        $hasError={hasError}
        animate={hasError ? { x: [-10, 10, -10, 10, 0] } : {}}
        transition={{ duration: 0.4 }}
      >
        {label && (
          <FloatingLabel
            $isFocused={isFocused}
            $hasValue={hasValue}
            $hasError={hasError}
          >
            {label}
            {required && <RequiredIndicator>*</RequiredIndicator>}
          </FloatingLabel>
        )}
        <StyledInput
          $size={size}
          $hasError={hasError}
          $hasValue={hasValue}
          $isFocused={isFocused}
          value={value}
          defaultValue={defaultValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder}
          type={type}
        />
      </InputContainer>
      <AnimatePresence>
        {(error || helperText) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <HelperText $isError={hasError}>
              {error || helperText}
            </HelperText>
          </motion.div>
        )}
      </AnimatePresence>
    </InputWrapper>
  );
};

/**
 * NobleTextArea Component
 */
export const NobleTextArea: React.FC<NobleTextAreaProps> = ({
  label,
  error,
  helperText,
  value,
  defaultValue,
  required,
  disabled,
  placeholder,
  rows,
  onFocus,
  onBlur,
  onChange,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(value || defaultValue || '');
  const hasValue = !!inputValue || value !== undefined;
  const hasError = !!error;

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    onChange?.(e);
  };

  return (
    <InputWrapper>
      {label && (
        <div style={{ marginBottom: NobleTokens.spacing.xs }}>
          <span
            style={{
              fontSize: NobleTokens.typography.fontSize.bodySmall,
              fontWeight: NobleTokens.typography.fontWeight.medium,
              color: hasError
                ? NobleTokens.colors.semantic.criticalRed
                : NobleTokens.colors.text.primary,
            }}
          >
            {label}
            {required && <RequiredIndicator>*</RequiredIndicator>}
          </span>
        </div>
      )}
      <InputContainer
        $hasError={hasError}
        animate={hasError ? { x: [-10, 10, -10, 10, 0] } : {}}
        transition={{ duration: 0.4 }}
      >
        <StyledTextArea
          $hasError={hasError}
          $isFocused={isFocused}
          value={value}
          defaultValue={defaultValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder}
          rows={rows}
        />
      </InputContainer>
      <AnimatePresence>
        {(error || helperText) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <HelperText $isError={hasError}>
              {error || helperText}
            </HelperText>
          </motion.div>
        )}
      </AnimatePresence>
    </InputWrapper>
  );
};

/**
 * NobleInputNumber Component
 * For numeric inputs with industrial styling
 */
export const NobleInputNumber: React.FC<NobleInputNumberProps> = ({
  label,
  error,
  helperText,
  size = 'medium',
  required,
  value,
  defaultValue,
  min,
  max,
  step,
  placeholder,
  disabled,
  onChange,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasError = !!error;

  return (
    <InputWrapper>
      {label && (
        <div style={{ marginBottom: NobleTokens.spacing.xs }}>
          <span
            style={{
              fontSize: NobleTokens.typography.fontSize.bodySmall,
              fontWeight: NobleTokens.typography.fontWeight.medium,
              color: hasError
                ? NobleTokens.colors.semantic.criticalRed
                : NobleTokens.colors.text.primary,
            }}
          >
            {label}
            {required && <RequiredIndicator>*</RequiredIndicator>}
          </span>
        </div>
      )}
      <InputContainer
        $hasError={hasError}
        animate={hasError ? { x: [-10, 10, -10, 10, 0] } : {}}
        transition={{ duration: 0.4 }}
      >
        <InputNumber
          value={value}
          defaultValue={defaultValue}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          disabled={disabled}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          style={{
            width: '100%',
            height: size === 'small' ? '40px' : size === 'large' ? '56px' : '48px',
            background: isFocused
              ? NobleTokens.colors.background.level1Light
              : NobleTokens.colors.background.level0Light,
            border: `2px solid ${
              hasError
                ? NobleTokens.colors.semantic.criticalRed
                : isFocused
                ? NobleTokens.colors.primary.commandBlue
                : 'transparent'
            }`,
            borderRadius: NobleTokens.borderRadius.md,
            boxShadow: hasError
              ? NobleTokens.shadows.focusRed
              : isFocused
              ? NobleTokens.shadows.focusBlue
              : NobleTokens.shadows.neumorphicInset,
          }}
        />
      </InputContainer>
      <AnimatePresence>
        {(error || helperText) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <HelperText $isError={hasError}>
              {error || helperText}
            </HelperText>
          </motion.div>
        )}
      </AnimatePresence>
    </InputWrapper>
  );
};

export default NobleInput;

